import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { restoreScroll } from "../../lib/scrollMemory";

// Scroll position per history entry (location.key) for this app session.
const positions = new Map();

/**
 * A new screen (pathname change) opens at the top. Back / forward (POP)
 * returns to where that screen was left — e.g. a product listing after the
 * back button on a product. URL changes within one screen (listing filters,
 * written with `replace`) keep the scroll as it is.
 *
 * The browser's own restoration is switched off: it raced the old
 * scroll-to-top and the late-loading listing and landed half-way down.
 * Hash links (#section) are intentionally skipped so in-page anchors
 * still work as expected.
 */
export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const lastY = useRef(0);                       // last scroll position of the screen on display
  const shown = useRef({ key, pathname });

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const onScroll = () => { lastY.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const prev = shown.current;
    shown.current = { key, pathname };
    // Remember where the screen being left was. Read from the scroll events,
    // not window.scrollY: the new screen's DOM is already in place here.
    if (prev.key !== key) positions.set(prev.key, lastY.current);
    if (hash) return undefined;
    if (navigationType === "POP" && positions.has(key)) return restoreScroll(positions.get(key));
    if (prev.key !== key && prev.pathname === pathname) return undefined;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    lastY.current = 0;
    return undefined;
  }, [pathname, hash, key, navigationType]);

  return null;
}
